<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAudienceStore } from '@/app/stores/audience'
import VfAppShell from '@/shared/ui/VfAppShell.vue'
import VfImagePreview from '@/shared/ui/VfImagePreview.vue'
import { emitTelemetry } from '@/core/telemetry/telemetry'
import { useTheme } from '@/shared/theme/useTheme'
import VfActionButton from '@/shared/ui/VfActionButton.vue'
import { localizedAudienceText } from '@/core/i18n/audienceText'

const audience = useAudienceStore()
const route = useRoute()
const router = useRouter()
const imagePreview = ref<{ src: string; alt: string; width: number; height: number } | null>(null)
const pressTargetSelector = 'button:not(:disabled), a[href], [role="button"]:not([aria-disabled="true"])'
let pressedTarget: HTMLElement | null = null
let pressedPointerId: number | null = null
let pressOrigin = { x: 0, y: 0 }
let imageObserver: MutationObserver | null = null
useTheme()

function clearPressedTarget() {
  pressedTarget?.classList.remove('is-pressed')
  pressedTarget = null
  pressedPointerId = null
}

function handlePointerDown(event: PointerEvent) {
  if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return
  const source = event.target
  if (!(source instanceof Element)) return
  const target = source.closest<HTMLElement>(pressTargetSelector)
  if (!target) return
  clearPressedTarget()
  pressedTarget = target
  pressedPointerId = event.pointerId
  pressOrigin = { x: event.clientX, y: event.clientY }
  target.classList.add('is-pressed')
}

function handlePointerMove(event: PointerEvent) {
  if (event.pointerId !== pressedPointerId) return
  if (Math.hypot(event.clientX - pressOrigin.x, event.clientY - pressOrigin.y) > 10) clearPressedTarget()
}

function isPagePreviewImage(image: HTMLImageElement | null): image is HTMLImageElement {
  return Boolean(image && !image.matches('[role="button"], [tabindex]') && image.closest('.vf-phone-shell') && !image.closest('button, a, [aria-hidden="true"], .vf-broadcast-modal, .vf-broadcast-detail-modal, .vf-emergency-modal, .pswp, .vf-svg-preview'))
}

function openPageImage(image: HTMLImageElement) {
  const src = image.currentSrc || image.src || image.getAttribute('src') || ''
  if (!src) return
  imagePreview.value = { src, alt: image.alt || '图片', width: image.naturalWidth, height: image.naturalHeight }
}

function previewImageFromPage(event: MouseEvent) {
  if (audience.previewMode || event.defaultPrevented) return
  const target = event.target
  if (!(target instanceof Element)) return
  const image = target.closest<HTMLImageElement>('img')
  if (!isPagePreviewImage(image)) return
  event.preventDefault()
  openPageImage(image)
}

function previewImageFromKeyboard(event: KeyboardEvent) {
  if (audience.previewMode || event.defaultPrevented || (event.key !== 'Enter' && event.key !== ' ')) return
  const trigger = event.target instanceof HTMLElement && event.target.dataset.vfImagePreview === 'true' ? event.target : null
  const image = trigger?.querySelector<HTMLImageElement>('img') || null
  if (!isPagePreviewImage(image)) return
  event.preventDefault()
  openPageImage(image)
}

function decoratePreviewableImages(root: Document | Element = document) {
  const images = root instanceof HTMLImageElement ? [root] : [...root.querySelectorAll<HTMLImageElement>('img')]
  for (const image of images) {
    if (!isPagePreviewImage(image)) continue
    const trigger = image.parentElement
    if (!trigger) continue
    trigger.tabIndex = 0
    trigger.setAttribute('role', 'button')
    trigger.dataset.vfImagePreview = 'true'
    const action = localizedAudienceText(audience.config, audience.locale, '点击放大')
    trigger.setAttribute('aria-label', `${image.alt || (audience.locale === 'en' ? 'Image' : '图片')}，${action}`)
  }
}

function observePreviewableImages() {
  decoratePreviewableImages()
  imageObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) decoratePreviewableImages(node)
      }
    }
  })
  imageObserver.observe(document.body, { childList: true, subtree: true })
}

async function bootstrap() {
  await audience.bootstrap()
  if (audience.status !== 'ready' || route.name !== 'home') return
  const params = new URLSearchParams(window.location.search)
  const mode = params.get('mode')
  if (params.get('restorePage') !== 'summary' || (mode !== 'entry' && mode !== 'exit')) return
  const snapshot = audience.restoreSharedRoute(mode, {}, window.location.search)
  const hash = snapshot ? audience.sharedRouteHash(snapshot) : ''
  if (hash) await router.replace(hash)
}

onMounted(() => {
  void bootstrap()
  document.addEventListener('click', previewImageFromPage)
  document.addEventListener('keydown', previewImageFromKeyboard)
  document.addEventListener('pointerdown', handlePointerDown, { passive: true })
  document.addEventListener('pointermove', handlePointerMove, { passive: true })
  document.addEventListener('pointerup', clearPressedTarget, { passive: true })
  document.addEventListener('pointercancel', clearPressedTarget, { passive: true })
  window.addEventListener('scroll', clearPressedTarget, { passive: true, capture: true })
  observePreviewableImages()
})
onBeforeUnmount(() => {
  clearPressedTarget()
  imageObserver?.disconnect()
  document.removeEventListener('click', previewImageFromPage)
  document.removeEventListener('keydown', previewImageFromKeyboard)
  document.removeEventListener('pointerdown', handlePointerDown)
  document.removeEventListener('pointermove', handlePointerMove)
  document.removeEventListener('pointerup', clearPressedTarget)
  document.removeEventListener('pointercancel', clearPressedTarget)
  window.removeEventListener('scroll', clearPressedTarget, true)
})
watch([() => audience.status, () => route.fullPath], ([status, path], previous) => {
  if (status !== 'ready' || (previous && previous[0] === status && previous[1] === path)) return
  emitTelemetry('page_view', {
    stadiumId: audience.stadiumId, eventId: audience.config?._runtime?.eventId || '', releaseId: audience.releaseId, preview: audience.previewMode,
  }, { path })
})
</script>

<template>
  <VfAppShell>
    <section v-if="audience.status === 'loading' || audience.status === 'idle'" class="vf-status-card" role="status">
      <strong>正在加载活动内容</strong>
      <span>正在确认当前活动的发布配置，请稍候。</span>
    </section>
    <section v-else-if="audience.status === 'error'" class="vf-status-card vf-status-card--error" role="alert">
      <strong>暂时无法打开活动</strong>
      <span>{{ audience.error }}</span>
      <VfActionButton class="vf-primary-action" @click="bootstrap">重新加载</VfActionButton>
    </section>
    <RouterView v-else />
  </VfAppShell>
  <VfImagePreview v-if="imagePreview" :src="imagePreview.src" :alt="imagePreview.alt" :width="imagePreview.width" :height="imagePreview.height" @close="imagePreview = null" />
</template>
